import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Tabs, message } from 'antd';
import { PersistentStorage } from '../../../utils/persistentStorage';
import { useIsMobile } from '../../../utils/responsiveUtils';
import { ProcurementDashboard } from '../components/ProcurementDashboard';
import { PurchaseRequisitions } from '../components/PurchaseRequisitions';
import { PurchaseOrders } from '../components/PurchaseOrders';
import { VendorManagement } from '../components/VendorManagement';
import { GoodsReceipts } from '../components/GoodsReceipts';
import { VendorInvoices } from '../components/VendorInvoices';
import { WorkflowBuilder } from '../components/WorkflowBuilder';
import { ItemMaster } from '../components/ItemMaster';
import { PriceListManagement } from '../components/PriceListManagement';
import { BuyingSettings } from '../components/BuyingSettings';
import type { 
  PurchaseRequisition,
  PurchaseOrder,
  Vendor,
  VendorInvoice,
  GoodsReceipt,
  ApprovalFlow,
  ItemMaster as ItemMasterType,
  PriceList,
  BuyingSettings as BuyingSettingsType,
} from '../types';

const { TabPane } = Tabs;

export const ProcurementOverview: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Set active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/purchase/requisitions')) return 'requisitions';
    if (path.includes('/purchase/orders')) return 'orders';
    if (path.includes('/purchase/vendors')) return 'vendors';
    if (path.includes('/purchase/receipts')) return 'receipts';
    if (path.includes('/purchase/invoices')) return 'invoices';
    if (path.includes('/purchase/workflows')) return 'workflows';
    if (path.includes('/purchase/items')) return 'items';
    if (path.includes('/purchase/pricelists')) return 'pricelists';
    if (path.includes('/purchase/settings')) return 'settings';
    return 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [requisitions, setRequisitions] = useState<PurchaseRequisition[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [invoices, setInvoices] = useState<VendorInvoice[]>([]);
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [workflows, setWorkflows] = useState<ApprovalFlow[]>([]);
  const [items, setItems] = useState<ItemMasterType[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [buyingSettings, setBuyingSettings] = useState<BuyingSettingsType | null>(null);

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  // Mock data initialization
  useEffect(() => {
    // Initialize mock workflows
    const mockWorkflows: ApprovalFlow[] = [
      {
        id: '1',
        name: 'Purchase Requisition Approval',
        description: 'Standard approval flow for purchase requisitions',
        type: 'sequential',
        documentType: 'purchase_requisition',
        isActive: true,
        steps: [
          {
            id: 'step1',
            stepNumber: 1,
            name: 'Department Manager Approval',
            description: 'Department manager reviews and approves the requisition',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver1',
                type: 'manager',
                userId: 'manager1',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 24,
            escalationRules: [],
            isActive: true,
          },
          {
            id: 'step2',
            stepNumber: 2,
            name: 'Finance Review',
            description: 'Finance team reviews budget and compliance',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver2',
                type: 'role',
                roleId: 'finance_director',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 48,
            escalationRules: [],
            isActive: true,
          },
          {
            id: 'step3',
            stepNumber: 3,
            name: 'Executive Approval',
            description: 'Executive approval for high-value requisitions',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver3',
                type: 'role',
                roleId: 'executive_vp',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 72,
            escalationRules: [],
            conditions: [
              {
                id: 'condition1',
                field: 'totalAmount',
                operator: 'greater_than',
                value: 10000,
              },
            ],
            isActive: true,
          },
        ],
        conditions: [],
        createdBy: 'admin',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: '2',
        name: 'Purchase Order Approval',
        description: 'Approval flow for purchase orders',
        type: 'sequential',
        documentType: 'purchase_order',
        isActive: true,
        steps: [
          {
            id: 'step1',
            stepNumber: 1,
            name: 'Procurement Manager',
            description: 'Procurement manager reviews vendor selection and terms',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver1',
                type: 'role',
                roleId: 'procurement_manager',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 24,
            escalationRules: [],
            isActive: true,
          },
          {
            id: 'step2',
            stepNumber: 2,
            name: 'Finance Approval',
            description: 'Finance approval for payment terms and budget',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver2',
                type: 'role',
                roleId: 'finance_director',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 48,
            escalationRules: [],
            isActive: true,
          },
        ],
        conditions: [],
        createdBy: 'admin',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: '3',
        name: 'Vendor Invoice Approval',
        description: 'Three-way matching approval for vendor invoices',
        type: 'parallel',
        documentType: 'vendor_invoice',
        isActive: true,
        steps: [
          {
            id: 'step1',
            stepNumber: 1,
            name: 'Invoice Verification',
            description: 'Verify invoice details against PO and receipt',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver1',
                type: 'role',
                roleId: 'accounts_payable',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 24,
            escalationRules: [],
            isActive: true,
          },
          {
            id: 'step2',
            stepNumber: 2,
            name: 'Payment Authorization',
            description: 'Authorize payment processing',
            type: 'single_approver',
            approvers: [
              {
                id: 'approver2',
                type: 'role',
                roleId: 'finance_director',
                isBackup: false,
                order: 1,
              },
            ],
            requiredApprovals: 1,
            timeoutHours: 48,
            escalationRules: [],
            isActive: true,
          },
        ],
        conditions: [],
        createdBy: 'admin',
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    // Initialize mock vendors
    const mockVendors: Vendor[] = [
      {
        id: '1',
        code: 'VEN001',
        name: 'ABC Supplies Inc.',
        type: 'supplier',
        category: 'raw_materials',
        status: 'active',
        contactInfo: {
          primaryContact: {
            name: 'John Smith',
            title: 'Sales Manager',
            email: 'john.smith@abcsupplies.com',
            phone: '+1-555-0123',
          },
        },
        address: {
          billing: {
            street: '123 Business Ave',
            city: 'Los Angeles',
            state: 'CA',
            postalCode: '90210',
            country: 'USA',
          },
        },
        paymentTerms: {
          termCode: 'NET30',
          description: 'Net 30 days',
          daysNet: 30,
          currency: 'USD',
          paymentMethod: 'bank_transfer',
        },
        taxInfo: {
          taxId: '12-3456789',
          taxExempt: false,
          taxCategory: 'standard',
        },
        documents: [],
        rating: {
          overall: 4.5,
          quality: 4.6,
          delivery: 4.3,
          service: 4.7,
          price: 4.2,
          totalOrders: 25,
          onTimeDelivery: 95,
          lastEvaluated: new Date(),
        },
        certifications: [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'admin',
      },
      {
        id: '2',
        code: 'VEN002',
        name: 'Tech Solutions Ltd.',
        type: 'supplier',
        category: 'equipment',
        status: 'active',
        contactInfo: {
          primaryContact: {
            name: 'Sarah Johnson',
            title: 'Account Manager',
            email: 'sarah.johnson@techsolutions.com',
            phone: '+1-555-0456',
          },
        },
        address: {
          billing: {
            street: '456 Tech Park',
            city: 'San Francisco',
            state: 'CA',
            postalCode: '94102',
            country: 'USA',
          },
        },
        paymentTerms: {
          termCode: 'NET45',
          description: 'Net 45 days',
          daysNet: 45,
          currency: 'USD',
          paymentMethod: 'bank_transfer',
        },
        taxInfo: {
          taxId: '98-7654321',
          taxExempt: false,
          taxCategory: 'standard',
        },
        documents: [],
        rating: {
          overall: 4.2,
          quality: 4.1,
          delivery: 4.0,
          service: 4.5,
          price: 4.3,
          totalOrders: 18,
          onTimeDelivery: 88,
          lastEvaluated: new Date(),
        },
        certifications: [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'admin',
      },
    ];

    setWorkflows(mockWorkflows);
    // Initialize with persistent vendors data, fallback to mock data if empty
    const persistentVendors = PersistentStorage.initializeWithMockData('vendors', mockVendors);
    setVendors(persistentVendors);
  }, []);

  const handleCreateRequisition = () => {
    message.info('Create Purchase Requisition dialog would open here');
  };

  const handleCreatePurchaseOrder = () => {
    message.info('Create Purchase Order dialog would open here');
  };

  const handleManageVendors = () => {
    setActiveTab('vendors');
  };

  const handleManageWorkflows = () => {
    setActiveTab('workflows');
  };

  const handleWorkflowCreate = async (workflow: Partial<ApprovalFlow>) => {
    try {
      const newWorkflow: ApprovalFlow = {
        ...workflow,
        id: Date.now().toString(),
        steps: [],
        conditions: [],
        createdBy: 'current-user',
        createdAt: new Date(),
        updatedAt: new Date(),
      } as ApprovalFlow;

      setWorkflows(prev => [...prev, newWorkflow]);
      message.success('Workflow created successfully');
    } catch (error) {
      message.error('Failed to create workflow');
    }
  };

  const handleWorkflowUpdate = async (id: string, workflow: Partial<ApprovalFlow>) => {
    try {
      setWorkflows(prev =>
        prev.map(w => w.id === id ? { ...w, ...workflow, updatedAt: new Date() } : w)
      );
      message.success('Workflow updated successfully');
    } catch (error) {
      message.error('Failed to update workflow');
    }
  };

  const handleWorkflowDelete = async (id: string) => {
    try {
      setWorkflows(prev => prev.filter(w => w.id !== id));
      message.success('Workflow deleted successfully');
    } catch (error) {
      message.error('Failed to delete workflow');
    }
  };

  const handleWorkflowActivate = async (id: string) => {
    try {
      setWorkflows(prev =>
        prev.map(w => w.id === id ? { ...w, isActive: true, updatedAt: new Date() } : w)
      );
      message.success('Workflow activated successfully');
    } catch (error) {
      message.error('Failed to activate workflow');
    }
  };

  const handleWorkflowDeactivate = async (id: string) => {
    try {
      setWorkflows(prev =>
        prev.map(w => w.id === id ? { ...w, isActive: false, updatedAt: new Date() } : w)
      );
      message.success('Workflow deactivated successfully');
    } catch (error) {
      message.error('Failed to deactivate workflow');
    }
  };

  return (
    <div className="procurement-overview">
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        size="large"
        style={{ minHeight: '600px' }}
      >
        <TabPane tab="📊 Dashboard" key="dashboard">
          <ProcurementDashboard
            onCreateRequisition={handleCreateRequisition}
            onCreatePurchaseOrder={handleCreatePurchaseOrder}
            onManageVendors={handleManageVendors}
            onManageWorkflows={handleManageWorkflows}
          />
        </TabPane>
        
        <TabPane tab="📝 Purchase Requisitions" key="requisitions">
          <PurchaseRequisitions
            requisitions={requisitions}
            onRequisitionCreate={handleCreateRequisition}
            onRequisitionUpdate={(id, data) => {
              // Handle requisition update
              message.success('Requisition updated successfully');
            }}
            onRequisitionDelete={(id) => {
              // Handle requisition delete
              message.success('Requisition deleted successfully');
            }}
          />
        </TabPane>
        
        <TabPane tab="🛒 Purchase Orders" key="orders">
          <PurchaseOrders
            purchaseOrders={purchaseOrders}
            vendors={vendors}
            onOrderCreate={handleCreatePurchaseOrder}
            onOrderUpdate={(id, data) => {
              // Handle order update
              message.success('Purchase order updated successfully');
            }}
            onOrderDelete={(id) => {
              // Handle order delete
              message.success('Purchase order deleted successfully');
            }}
          />
        </TabPane>
        
        <TabPane tab="🏢 Vendors" key="vendors">
          <VendorManagement
            vendors={vendors}
            onVendorCreate={(vendor) => {
              // Save new vendor to persistent storage
              const newVendor = {
                ...vendor,
                id: Date.now().toString(),
                createdAt: new Date(),
                updatedAt: new Date(),
                createdBy: 'current-user',
              };
              const updatedVendors = PersistentStorage.addToArray('vendors', newVendor, []);
              setVendors(updatedVendors);
              message.success('Vendor created successfully');
            }}
            onVendorUpdate={(id, data) => {
              // Update vendor in persistent storage
              const existingVendor = vendors.find(v => v.id === id);
              if (existingVendor) {
                const updatedVendor = { ...existingVendor, ...data, updatedAt: new Date() };
                const updatedVendors = PersistentStorage.updateArray('vendors', updatedVendor, []);
                setVendors(updatedVendors);
              }
              message.success('Vendor updated successfully');
            }}
            onVendorDelete={(id) => {
              // Remove vendor from persistent storage
              const updatedVendors = PersistentStorage.removeFromArray('vendors', id, []);
              setVendors(updatedVendors);
              message.success('Vendor deleted successfully');
            }}
          />
        </TabPane>
        
        <TabPane tab="📦 Goods Receipts" key="receipts">
          <GoodsReceipts
            receipts={receipts}
            purchaseOrders={purchaseOrders}
            onReceiptCreate={(receipt) => {
              // Handle receipt create
              message.success('Goods receipt created successfully');
            }}
            onReceiptUpdate={(id, data) => {
              // Handle receipt update
              message.success('Goods receipt updated successfully');
            }}
          />
        </TabPane>
        
        <TabPane tab="💰 Vendor Invoices" key="invoices">
          <VendorInvoices
            invoices={invoices}
            vendors={vendors}
            purchaseOrders={purchaseOrders}
            onInvoiceCreate={(invoice) => {
              // Handle invoice create
              message.success('Invoice created successfully');
            }}
            onInvoiceUpdate={(id, data) => {
              // Handle invoice update
              message.success('Invoice updated successfully');
            }}
            onInvoiceApprove={(id) => {
              // Handle invoice approval
              message.success('Invoice approved successfully');
            }}
          />
        </TabPane>
        
        <TabPane tab="⚙️ Workflows" key="workflows">
          <WorkflowBuilder
            workflows={workflows}
            onWorkflowCreate={handleWorkflowCreate}
            onWorkflowUpdate={handleWorkflowUpdate}
            onWorkflowDelete={handleWorkflowDelete}
            onWorkflowActivate={handleWorkflowActivate}
            onWorkflowDeactivate={handleWorkflowDeactivate}
          />
        </TabPane>
        
        <TabPane tab="📦 Item Master" key="items">
          <div>
            <p>Item Master component will be available once imports are properly configured.</p>
          </div>
        </TabPane>
        
        <TabPane tab="💰 Price Lists" key="pricelists">
          <div>
            <p>Price List Management component will be available once imports are properly configured.</p>
          </div>
        </TabPane>
        
        <TabPane tab="⚙️ Settings" key="settings">
          <div>
            <p>Buying Settings component will be available once imports are properly configured.</p>
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
}; 